import type {
  ConversationChatbotMessageAddedEvent,
  ConversationEmailMessageAddedEvent,
  ConversationFacebookMessengerMessageAddedEvent,
  ConversationMessengerMessageAddedEvent,
  ConversationTwitterMessageAddedEvent,
  ConversationWhatsAppMessageAddedEvent,
  ConversationWidgetChatMessageAddedEvent,
} from 'lib-shared-types-api';
import { ComponentProps, useCallback, useMemo, useState } from 'react';

import { ConversationEventGroupConfigBase } from 'components/conversation/conversationEventGroups/conversationEventGroups.types';
import { TranslateTextChangeData } from 'components/translateTextDropdownSelectionList';
import { TranslationBadge } from 'components/translationBadge';

import { ConversationTextualMessage } from '../../textualMessage/conversationTextualMessage';
import { ConversationTextualMessagePropBase } from '../../textualMessage/conversationTextualMessage.types';

import {
  ConversationHTMLEventTextWithTranslation,
  getEventLanguage,
} from './conversationHTMLEventTextWithTranslation';

/** Represents the conversation email event group configuration. */
export type ConversationHTMLEventGroupConfig = ConversationEventGroupConfigBase<
  | 'group:contact_form'
  | 'group:email'
  | 'group:messenger'
  | 'group:adachat'
  | 'group:digitalgeniuschat'
  | 'group:kindlychat'
  | 'group:genericapimessaging'
  | 'group:trustpilotreviews'
  | 'group:yotporeviews'
  | 'group:facebook_messenger'
  | 'group:whats_app'
  | 'group:widget_chat'
  | 'group:messenger'
  | 'group:sms'
  | 'group:twitter',
  Pick<
    ConversationHTMLEventGroupProps,
    'hideAttachments' | 'hideGutterActions' | 'stickyHeader'
  >
>;

type ConversationHTMLEventGroupProps = ConversationTextualMessagePropBase<
  | ConversationEmailMessageAddedEvent
  | ConversationMessengerMessageAddedEvent
  | ConversationChatbotMessageAddedEvent
  | ConversationFacebookMessengerMessageAddedEvent
  | ConversationMessengerMessageAddedEvent
  | ConversationWhatsAppMessageAddedEvent
  | ConversationWidgetChatMessageAddedEvent
  | ConversationTwitterMessageAddedEvent,
  Pick<
    ComponentProps<typeof ConversationTextualMessage.Events>,
    'hideAttachments'
  >
>;

/** Represents the component for displaying a conversation email event group. */
export const ConversationHTMLEventGroup = ({
  author,
  className,
  events,
  hideAttachments,
  hideGutterActions,
  stickyHeader,
}: ConversationHTMLEventGroupProps): JSX.Element => {
  const timestamp = events[events.length - 1].created_at;
  const [eventTranslations, setEventTranslations] = useState<
    Record<string, TranslateTextChangeData | undefined>
  >({});

  const handleTranslationChange = useCallback(
    (eventId: string, translation: TranslateTextChangeData | undefined) => {
      setEventTranslations((prev) => ({ ...prev, [eventId]: translation }));
    },
    [],
  );

  const groupTranslation = useMemo((): TranslateTextChangeData | undefined => {
    const translations = Object.values(eventTranslations).filter(
      (t): t is TranslateTextChangeData => t !== undefined,
    );
    if (translations.length === 0) return undefined;

    for (const status of [
      'pending',
      'rejected',
      'fulfilled',
      'notNeeded',
    ] as const) {
      const match = translations.find((t) => t.status === status);
      if (match) return match;
    }
    return undefined;
  }, [eventTranslations]);

  return (
    <ConversationTextualMessage
      className={className}
      author={author}
      timestamp={timestamp}
      hideGutterActions={hideGutterActions}
      stickyHeader={stickyHeader}
      variant={
        events[0].direction === 'outbound'
          ? 'textualOutbound'
          : 'textualInbound'
      }
      authorEndSlot={
        groupTranslation ? (
          <TranslationBadge
            translation={groupTranslation}
            originalLanguageCode={getEventLanguage(events[0]) ?? undefined}
            errorMessage="There was an error translating this message."
          />
        ) : undefined
      }
    >
      <ConversationTextualMessage.Events
        events={events}
        hideAttachments={hideAttachments}
        renderEventText={(event) => (
          <ConversationHTMLEventTextWithTranslation
            event={event}
            onTranslationChange={handleTranslationChange}
          />
        )}
      />
    </ConversationTextualMessage>
  );
};
